import React, { FormEvent, useContext, useState } from 'react'
import { FormCheckboxField, FormSelectField } from '@/components/Form/FormField'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { ClientAreaApiClient } from '@/utils/api/clientAreaApi'
import { toBase64 } from '@/utils/file.helpers'
import { Upload } from 'antd'
import { Language } from 'api/Clients/Types/Language'
import { NotificationImage } from 'api/Clients/Types/NotificationDto'
import { infoToast, successToast, ToastContext } from 'components/Toast/Toast'
import { useApiClient } from 'utils/api/ApiClient'

import { NotificationBoxWrapper } from './Notification.styles'

interface NotificationImageUploadModalProps {
  visible: boolean
  notificationId: string
  image?: NotificationImage
  languages: Language[]
  onForceRefresh(): void
  onClose(): void
}

export const NotificationImageUploadModal: React.FC<NotificationImageUploadModalProps> = (
  props
) => {
  const { image, languages } = props

  const clientAreaApiClient = useApiClient(ClientAreaApiClient)
  const setToast = useContext(ToastContext)

  const [isDefault, setIsDefault] = useState<boolean>(true)
  const [language, setLanguage] = useState<string>('en')
  const [fileInfo, setFileInfo] = useState<File>()
  const [additionalFileInfo, setAdditionalFileInfo] = useState<File>()
  const [filePreview, setFilePreview] = useState<string>()
  const [additionalFilePreview, setAdditionalFilePreview] = useState<string>()

  const preventSubmit = !image && !fileInfo && !additionalFileInfo
  const formId = 'notification-image-upload-form'
  const languageOptions = languages
    .filter((languageItem) => languageItem.clientAreaVisible)
    .map((languageItem) => ({ id: languageItem.id, name: languageItem.name }))

  const setSelectedFilePreview = (
    file: File,
    setPreview: React.Dispatch<React.SetStateAction<string | undefined>>
  ) => {
    setPreview(undefined)
    toBase64(file)
      .then((preview) => {
        if (typeof preview === 'string') {
          setPreview(preview)
        }
      })
      .catch((error) => {
        console.error(error)
        setToast(infoToast('Error during image preview'))
      })
  }

  const handleSelectMainFile = (file: File) => {
    if (!isValidImageType(file)) {
      setToast(infoToast('Please select a main image in JPEG, PNG or SVG format.'))
      return false
    } else {
      setFileInfo(file)
      setSelectedFilePreview(file, setFilePreview)
    }
    return false
  }

  const handleSelectAdditionalFile = (file: File) => {
    if (!isValidImageType(file)) {
      setToast(infoToast('Please select a main image in JPEG, PNG or SVG format.'))
      return false
    } else {
      setAdditionalFileInfo(file)
      setSelectedFilePreview(file, setAdditionalFilePreview)
    }
    return false
  }

  const handleUploadFile = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    if (!fileInfo) {
      setToast(infoToast('Please select a main file before submitting.'))
      return
    }

    if (image || fileInfo) {
      try {
        if (preventSubmit) {
          setToast(infoToast('Please select language'))
          return
        }
        const base64 = fileInfo ? ((await toBase64(fileInfo)) as string) : ''
        const additionalBase64 = additionalFileInfo
          ? ((await toBase64(additionalFileInfo)) as string)
          : null
        if (image) {
          await clientAreaApiClient.replaceNotificationImage(props.notificationId, image.id, {
            image: base64,
            updateImage: !!base64,
            additionalImage: additionalBase64,
            updateAdditionalImage: !!additionalBase64,
            isDefault,
          })
          setToast(successToast('Image updated successfully'))
        } else {
          await clientAreaApiClient.uploadNotificationImage(props.notificationId, {
            languageId: language,
            image: base64 as string,
            additionalImage: additionalBase64,
            isDefault,
          })
          setToast(successToast('File uploaded successfully for language' + ' ' + language))
        }
        props.onClose()
        props.onForceRefresh()
      } catch (e) {
        console.error(e)
        setToast(infoToast('Error during upload'))
      }
    }
  }

  return (
    <Dialog open={props.visible} onOpenChange={(open) => !open && props.onClose()}>
      <DialogContent size='md'>
        <DialogHeader>
          <DialogTitle>
            {image ? 'Replace Notification Image' : 'Upload Notification Image'}
          </DialogTitle>
        </DialogHeader>
        <form id={formId} className='flex flex-col gap-4' onSubmit={handleUploadFile}>
          <Upload
            beforeUpload={handleSelectMainFile}
            showUploadList={false}
            maxCount={1}
            fileList={[]}
            accept='.jpg,.jpeg,.png,.svg'
          >
            <Button type='button'>Select Main File</Button>
          </Upload>
          <Upload
            beforeUpload={handleSelectAdditionalFile}
            showUploadList={false}
            maxCount={1}
            fileList={[]}
            accept='.jpg,.jpeg,.png,.svg'
          >
            <Button type='button' variant='outline'>
              Select Additional File
            </Button>
          </Upload>
          <div className='space-y-2'>
            <div className='text-sm font-medium'>Preview</div>
            <NotificationBoxWrapper>
              {filePreview && (
                <img
                  src={filePreview}
                  alt='file'
                  style={{ width: 300, maxHeight: 300, display: 'flex', margin: 'auto' }}
                />
              )}
              {additionalFilePreview && (
                <img
                  src={additionalFilePreview}
                  alt='additional-file'
                  style={{ width: 300, maxHeight: 300, display: 'flex', margin: 'auto' }}
                />
              )}
            </NotificationBoxWrapper>
          </div>
          <FormCheckboxField
            name='isDefault'
            label='Is Default'
            value={isDefault}
            dataTest='is-silent-field'
            onChange={setIsDefault}
          />
          {!image && (
            <FormSelectField
              name='language'
              label='Language'
              options={languageOptions}
              value={language}
              dataTest='language-field'
              onChange={(value) => setLanguage(String(value))}
            />
          )}
        </form>
        <DialogFooter>
          <Button variant='outline' type='button' onClick={props.onClose}>
            Cancel
          </Button>
          <Button disabled={preventSubmit} form={formId} key='button' type='submit'>
            Submit
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

const isValidImageType = (file: File): boolean => {
  const allowedTypes = ['image/jpeg', 'image/png', 'image/svg+xml']
  return allowedTypes.includes(file.type)
}
